import React from "react";
import CleverIcon from "../../assets/CleverIcon";
import { useAppSelector } from "../../app/hooks";
import { useOAuth2 } from "@tasoskakour/react-use-oauth2";
import ClassLinkIcon from "../../assets/ClassLinkIcon";

interface OAuth2Props {
	authorizeUrl: string;
	clientId: string;
	scope: string;
	exchangeUrl: string;
}

interface LoginWithOutsideSiteButtonProps {
	id: string;
	siteName: string;
	onSuccessfulLogin: () => void;
	onLoginError: (error: string) => void;
}
export function LoginWithOutsideSiteButton({
	id,
	siteName,
	onSuccessfulLogin,
	onLoginError,
}: LoginWithOutsideSiteButtonProps) {
	const { aspUrl, config } = useAppSelector((state) => state.site);

	const oAuth2Props: OAuth2Props = {
		authorizeUrl: "",
		clientId: "",
		scope: "",
		exchangeUrl: "",
	};

	const redirectUriBuilder = (aspUrl: string) => {
		const originUrl = new URL(document.location.origin);
		const redirectUri =
			originUrl.port !== ""
				? `${document.location.origin}/spa/oauthCallback`
				: `${aspUrl}/spa/oauthCallback`;
		return redirectUri;
	};

	if (siteName === "Clever") {
		oAuth2Props.authorizeUrl = "https://clever.com/oauth/authorize";
		oAuth2Props.clientId = config?.cleverClientId ?? "77039d3b09419608e19a";
		oAuth2Props.scope = "user";
		oAuth2Props.exchangeUrl = `${aspUrl}/signin-clever-for-spa`;
	}
	if (siteName === "ClassLink") {
		oAuth2Props.authorizeUrl = "https://launchpad.classlink.com/oauth2/v2/auth";
		oAuth2Props.clientId =
			config?.classLinkClientId ?? "c168968588479102da1b12d1f70ebe95045e36754f5a";
		oAuth2Props.scope = "full";
		oAuth2Props.exchangeUrl = `${aspUrl}/signin-classlink-for-spa`;
	}

	const { data, loading, error, getAuth, logout } = useOAuth2({
		authorizeUrl: oAuth2Props.authorizeUrl,
		clientId: oAuth2Props.clientId,
		redirectUri: redirectUriBuilder(aspUrl),
		scope: oAuth2Props.scope,
		responseType: "code",
		exchangeCodeForTokenQuery: {
			url: oAuth2Props.exchangeUrl,
			method: "POST",
			headers: new Headers({
				"X-Requested-With": "XMLHttpRequest",
				Accept: "application/json;profile=camelCase",
			}),
		},
		onSuccess: (payload: any) => {
			if (payload?.status === "OK") {
				onSuccessfulLogin();
			} else {
				if (payload?.error === "UserNotFound") {
					onLoginError(
						"Sorry, we cannot log you into MI Write. Please contact your system administrator."
					);
				}
				console.error("useOAuth2.onSuccess failed:", JSON.stringify(payload));
			}
		},
		onError: (error_: string) => {
			console.error("useOAuth2.onError: ", error_);
		},
	});

	const handleClick = () => {
		getAuth();
	};

	const isLoggedIntoOutsideSite = Boolean(data?.access_token);
	const buttonClass = error
		? "bg-pink-100"
		: loading
			? "bg-green-100"
			: "bg-zinc-200 hover:bg-zinc-300";
	const buttonText = error
		? `Log in with ${siteName} (error)`
		: loading
			? "Logging in..."
			: isLoggedIntoOutsideSite
				? "Log out"
				: `Log in with ${siteName}`;

	return (
		<button
			title={`Log in using your ${siteName} account`}
			className={`font-button font-medium rounded-md text-gray-700 hover:text-gray-800 w-3/4 flex flex-nowrap justify-between items-center h-12 overflow-hidden ${buttonClass}`}
			disabled={loading}
			onClick={() => {
				console.log("before getAuth()");
				handleClick();
				console.log("after getAuth()");
			}}>
			{siteName === "Clever" ? (
				<CleverIcon className="h-full w-14 p-3 fill-white bg-[#1464FF]"></CleverIcon>
			) : (
				<ClassLinkIcon className="h-full w-14 p-3 fill-white bg-[#0a4d7f]"></ClassLinkIcon>
			)}

			<span className="w-full text-center">{buttonText}</span>
		</button>
	);
}
